import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Gift, AlertCircle } from 'lucide-react';
import { API_BASE_URL } from '../config'

interface RedemptionResult {
  message: string;
  credits_awarded: number;
  new_balance: number;
}

interface FailedAttempt {
  count: number;
  timestamp: number;
}

const ATTEMPT_LIMIT = 5;
const LOCKOUT_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds

const RedeemCouponPage: React.FC = () => {
  const [couponCode, setCouponCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<RedemptionResult | null>(null);
  const [isLocked, setIsLocked] = useState(false);
  const [remainingTime, setRemainingTime] = useState<number>(0);
  const { token, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user || !token) {
      navigate('/login', { replace: true });
    }
  }, [user, token, navigate]);

  useEffect(() => {
    // Check for existing failed attempts
    const checkLockoutStatus = () => {
      const storedAttempts = localStorage.getItem(`failedAttempts_${user?.id}`);
      if (storedAttempts) {
        const attempts: FailedAttempt = JSON.parse(storedAttempts);
        const timePassed = Date.now() - attempts.timestamp;
        
        if (attempts.count >= ATTEMPT_LIMIT && timePassed < LOCKOUT_DURATION) {
          setIsLocked(true);
          setRemainingTime(LOCKOUT_DURATION - timePassed);
        } else if (timePassed >= LOCKOUT_DURATION) {
          // Reset if lockout period has passed
          localStorage.removeItem(`failedAttempts_${user?.id}`);
          setIsLocked(false);
        }
      }
    };

    checkLockoutStatus();
  }, [user?.id]);

  // Update countdown timer
  useEffect(() => {
    if (!isLocked || remainingTime <= 0) return;

    const timer = setInterval(() => {
      setRemainingTime(prev => {
        const newTime = prev - 1000;
        if (newTime <= 0) {
          setIsLocked(false);
          localStorage.removeItem(`failedAttempts_${user?.id}`);
          clearInterval(timer);
        }
        return newTime;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isLocked, user?.id]);

  const updateFailedAttempts = () => {
    const storedAttempts = localStorage.getItem(`failedAttempts_${user?.id}`);
    let attempts: FailedAttempt;

    if (storedAttempts) {
      attempts = JSON.parse(storedAttempts);
      attempts.count += 1;
      attempts.timestamp = Date.now();
    } else {
      attempts = { count: 1, timestamp: Date.now() };
    }

    localStorage.setItem(`failedAttempts_${user?.id}`, JSON.stringify(attempts));

    if (attempts.count >= ATTEMPT_LIMIT) {
      setIsLocked(true);
      setRemainingTime(LOCKOUT_DURATION);
    }
  };

  if (!user || !token) {
    return null;
  }

  const formatTime = (ms: number) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLocked) return;

    setLoading(true);
    setError(null);
    setSuccess(null);


    try {
      const response = await fetch(`${API_BASE_URL}/api/coupon/redeem`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ code: couponCode })
      });

      const data = await response.json();

      if (!response.ok) {
        updateFailedAttempts();
        throw new Error(data.error || 'Failed to redeem coupon');
      }

      setSuccess(data);
      setCouponCode('');
      // Reset failed attempts on success
      localStorage.removeItem(`failedAttempts_${user?.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header section */}
        <div className="bg-white shadow rounded-lg mb-6 p-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Redeem Coupon</h1>
              <p className="text-sm text-gray-500">Enter your coupon code to receive credits</p>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 py-3 bg-white text-primary border border-primary text-lg rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 shadow-sm"
            >
              Back to Dashboard
            </button>
          </div>
        </div>

        {/* Main content section */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="max-w-xl mx-auto">
            <div className="flex items-center justify-center mb-6">
              <Gift className="h-12 w-12 text-primary" />
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="coupon-code" className="block text-sm font-medium text-gray-700">
                  Coupon Code
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    id="coupon-code"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary text-lg py-3 px-4"
                    placeholder="Enter your coupon code"
                    required
                    disabled={isLocked}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || isLocked}
                className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-lg font-medium text-white bg-primary hover:bg-accent focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary ${
                  (loading || isLocked) ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                ) : isLocked ? (
                  `Try again in ${formatTime(remainingTime)}`
                ) : (
                  'Redeem Coupon'
                )}
              </button>
            </form>

            {/* Error message */}
            {error && (
              <div className="mt-4 rounded-md bg-red-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">{error}</h3>
                    {!isLocked && (
                      <p className="mt-1 text-sm text-red-600">
                        Attempts remaining: {ATTEMPT_LIMIT - (Number(localStorage.getItem(`failedAttempts_${user?.id}`)) || 0)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Success message */}
            {success && (
              <div className="mt-4 rounded-md bg-green-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-green-800">{success.message}</h3>
                    <div className="mt-2 text-sm text-green-700">
                      <p>Credits awarded: {success.credits_awarded}</p>
                      <p>New balance: {success.new_balance}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default RedeemCouponPage;